import { makeStyles } from '@material-ui/core/styles';
import { Theme } from '@material-ui/core/styles';
import { TestDataAttributesConfigProvider, PreviewBackdrop, InvalidCertificate } from '@roc/ui';
import { observer } from 'mobx-react';
import { SnackbarProvider } from 'notistack';
import * as ReactDOM from 'react-dom';
import { Route, Router, Switch, useHistory } from 'react-router-dom';
import { BaseStore, Loader, MaintenanceWindowWorker, platformTokens, ThemeCongifurationContextProvider, useHeaderHeight, WrongCompanyBanner } from '@roc/feature-app-core';
import { StoreProvider } from '@roc/feature-app-core';
import {
  AppConfigurationsProvider,
  ThemeProvider,
} from '@roc/feature-app-core';
import {
  useBaseStore,
  Notification,
  RegisterWorker
} from '@roc/feature-app-core';
import AppCssBaseline, { AppCssBaselineProps } from './styles/AppCssBaseline';
import { useEffect } from 'react';
import './styles/styled-scrollbar.scss';
import './styles/platform-ag-grid.scss';
import { loadPlatformFont } from './styles/platformFont';
import { ThemeConfiguration } from '@roc/feature-types';

const isProduction = process.env.NODE_ENV === 'production';
const testDataAttributeConfig = {
  suffix: 'testid',
  enable: !isProduction,
};

// Toasts use the same tinted palette as the alert banners (platformTokens.status)
const toastStyle = (theme: Theme, tone: { bg: string; border: string; text: string }) => ({
  '&&': {
    backgroundColor: tone.bg,
    color: tone.text,
    border: `1px solid ${tone.border}`,
    borderRadius: 16,
    boxShadow: theme.shadows[9],
    fontWeight: 500,
    padding: theme.spacing(0.75, 2),
  },
  '&& #notistack-snackbar > svg': {
    color: tone.text,
  },
});

const useStyles = makeStyles((theme: Theme) => ({
  success: toastStyle(theme, platformTokens.status.success),
  error: toastStyle(theme, platformTokens.status.error),
  warning: toastStyle(theme, platformTokens.status.warning),
  info: toastStyle(theme, platformTokens.status.info),
  // sit just below the (floating) navigation bar
  topCenter: {
    top: (props: { headerHeight: number }) => props.headerHeight + 16,
  },
}));

const ScrollToTop = () => {
  const history = useHistory();
  useEffect(() => {
    const unlisten = history.listen((location, action) => {
      if (action !== 'POP') {
        window.scrollTo(0, 0);
      }
    });
    return () => unlisten();
  }, []);
  return null;
};

const AppContainer = observer(({ children }) => {
  const { globalStore } = useBaseStore();
  const headerHeight = useHeaderHeight();
  const classes = useStyles({ headerHeight });

  useEffect(() => {
    globalStore.validateHostCertificate()
  }, []);

  useEffect(() => {
    // add styled scrollbar to entire site
    document.body.classList.add('styled-scrollbar-all-child');
    // remove loading from index.html
    const el = document.getElementById('lds-ellipsis-container');
    if (el) el.remove();
  }, []);

  useEffect(() => {
    // embedded platform-ui toasts read this to sit below the navigation bar, like ours
    document.documentElement.style.setProperty('--platform-ui-toast-top', `${headerHeight + 16}px`);
  }, [headerHeight]);

  return (
    globalStore.isCertificateValid === false ?
      <InvalidCertificate />
      : <TestDataAttributesConfigProvider value={testDataAttributeConfig}>
        <SnackbarProvider
          classes={{
            variantSuccess: classes.success,
            variantError: classes.error,
            variantInfo: classes.info,
            variantWarning: classes.warning,
            containerAnchorOriginTopCenter: classes.topCenter,
          }}
          anchorOrigin={{
            horizontal: 'center',
            vertical: 'top'
          }}
          // same timing as the platform's sonner toasts: 4s on screen, 400ms in / 200ms out
          autoHideDuration={4000}
          transitionDuration={{ enter: 400, exit: 200 }}
        >
          {globalStore.isPreviewMode ? (
            <PreviewBackdrop />
          ) : null}
          {children}
          <Notification />
          <Loader />
        </SnackbarProvider>
      </TestDataAttributesConfigProvider>
  );
});

export interface RenderAppProps {
  store: BaseStore,
  App: any,
  appCssBaselineProps?: AppCssBaselineProps,
  ignoreApplyAppCssBaseline?: boolean,
  RouterComponent?: any,
  themeConfiguration?: ThemeConfiguration,
  ThemeProviderComponent?: typeof ThemeProvider,
  overridePrimaryColor?: string,
  AppContainerComponent?: any,
  AppCssBaselineComponent?: any
}

export const RenderApp = ({
  store,
  App,
  appCssBaselineProps,
  ignoreApplyAppCssBaseline = false,
  RouterComponent = Router,
  themeConfiguration,
  ThemeProviderComponent = ThemeProvider,
  overridePrimaryColor,
  AppContainerComponent = AppContainer,
  AppCssBaselineComponent = AppCssBaseline
}: RenderAppProps) => {
  loadPlatformFont();
  ReactDOM.render(
    <StoreProvider value={store}>
      <ThemeCongifurationContextProvider value={themeConfiguration}>
        <AppConfigurationsProvider overridePrimaryColor={overridePrimaryColor}>
          <ThemeProviderComponent>
            {
              !ignoreApplyAppCssBaseline && (<AppCssBaselineComponent {...appCssBaselineProps} />)
            }
            <RouterComponent history={store.globalStore.history}>
              <RegisterWorker />
              <MaintenanceWindowWorker />
              <WrongCompanyBanner />
              <ScrollToTop />
              <Switch>
                <Route
                  path={'/'}
                  render={() => (
                    <AppContainerComponent>
                      <App />
                    </AppContainerComponent>
                  )}
                />
              </Switch>
            </RouterComponent>
          </ThemeProviderComponent>
        </AppConfigurationsProvider>
      </ThemeCongifurationContextProvider>
    </StoreProvider>,
    document.getElementById('root')
  );
};
